/**
 * RecipeAllocationPreview Component
 *
 * User Story: US-P6-002 - Recipe Cost Integration
 * Step 10: Integration with Substrate Batch Creation
 *
 * Shows FIFO allocation preview for all ingredients in a recipe.
 * Displays which inventory batches will be consumed and estimated costs.
 *
 * Features:
 * - Preview allocation for each inventory-tracked ingredient
 * - Shows batch breakdown with quantities and costs
 * - Displays blended unit cost when multiple batches involved
 * - Insufficient stock warning per ingredient
 * - Total estimated material cost
 */

import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  Package,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  ChevronDown,
  ChevronUp,
  Droplets,
  Scale,
} from 'lucide-react'
import { useRecipeCost } from '@/hooks/useRecipeCost'

/**
 * Format a number as currency (GBP)
 */
function formatCurrency(value) {
  if (value === null || value === undefined) return '-'
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

/**
 * Format a number with appropriate precision
 */
function formatNumber(value, decimals = 2) {
  if (value === null || value === undefined) return '-'
  return new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  }).format(value)
}

/**
 * Format unit cost with more precision
 */
function formatUnitCost(value, unit = 'kg') {
  if (value === null || value === undefined || value === 0) return '-'
  const formatted = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(value)
  return `${formatted}/${unit}`
}

/**
 * Single ingredient allocation row
 *
 * Uses pre-calculated values from Edge Function (ratio-based calculation).
 * No local scaling needed - quantity_kg is already the correct amount for this batch.
 */
function IngredientAllocation({
  ingredient,
  isExpanded,
  onToggle,
}) {
  // Use pre-calculated values from Edge Function
  const quantityKg = ingredient.quantity_kg
  const totalCost = ingredient.total_cost || 0
  const unitCost = ingredient.unit_cost || ingredient.estimated_unit_cost || 0

  // Non-inventory tracked items (like water)
  if (!ingredient.track_inventory) {
    return (
      <div className="flex items-center justify-between py-3 border-b last:border-b-0">
        <div className="flex items-center gap-3">
          <Droplets className="h-4 w-4 text-sky-500" />
          <div>
            <div className="font-medium text-sm">{ingredient.item_name}</div>
            <div className="text-xs text-content-tertiary">No inventory tracking</div>
          </div>
        </div>
        <div className="text-right text-sm">
          <div className="font-mono">{formatNumber(quantityKg)} kg</div>
          <div className="text-xs text-content-tertiary">No cost</div>
        </div>
      </div>
    )
  }

  // Insufficient stock
  if (!ingredient.sufficient_stock) {
    return (
      <div className="py-3 border-b last:border-b-0 bg-amber-50/50 -mx-4 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <div>
              <div className="font-medium text-sm text-amber-900">{ingredient.item_name}</div>
              <div className="text-xs text-amber-700">
                Need {formatNumber(quantityKg)} kg, only {formatNumber(ingredient.available_stock)} kg available
              </div>
            </div>
          </div>
          <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200">
            Insufficient Stock
          </Badge>
        </div>
      </div>
    )
  }

  // Normal ingredient with sufficient stock
  return (
    <Collapsible open={isExpanded} onOpenChange={onToggle}>
      <CollapsibleTrigger className="w-full" asChild>
        <div className="flex items-center justify-between py-3 border-b last:border-b-0 cursor-pointer hover:bg-surface-secondary/50 -mx-4 px-4 transition-colors">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <div>
              <div className="font-medium text-sm">{ingredient.item_name}</div>
              <div className="text-xs text-content-tertiary">
                {formatNumber(quantityKg)} kg @ {formatUnitCost(unitCost)}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right text-sm">
              <div className="font-medium">{formatCurrency(totalCost)}</div>
            </div>
            {isExpanded ? (
              <ChevronUp className="h-4 w-4 text-content-tertiary" />
            ) : (
              <ChevronDown className="h-4 w-4 text-content-tertiary" />
            )}
          </div>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent className="bg-surface-secondary/30 -mx-4 px-4">
        <div className="py-3 pl-7 space-y-1 text-sm">
          <div className="text-xs text-content-tertiary uppercase tracking-wide mb-2">
            Batch Allocation (FIFO)
          </div>
          {/* Placeholder for detailed batch breakdown - would need actual allocation data */}
          <div className="text-content-secondary">
            Stock available: {formatNumber(ingredient.available_stock)} kg
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

/**
 * RecipeAllocationPreview Component
 *
 * Uses ratio-based recipe calculation:
 *   total_batch_kg = total_blocks × block_weight_kg
 *   total_parts = SUM(ingredient.parts)
 *   ingredient_kg = (ingredient.parts / total_parts) × total_batch_kg
 *
 * @param {Object} props
 * @param {string} props.recipeId - Recipe ID to preview
 * @param {number} props.totalBlocks - Number of blocks being created
 * @param {number} props.blockWeightKg - Weight per block in kg (from block format)
 * @param {Function} props.onAllocationChange - Callback when allocation status changes
 * @param {string} props.className - Additional CSS classes
 */
export default function RecipeAllocationPreview({
  recipeId,
  totalBlocks,
  blockWeightKg = 4.5,
  onAllocationChange,
  className = '',
}) {
  const { recipeCost, isLoading, error, calculateCost } = useRecipeCost()
  const [expandedIngredients, setExpandedIngredients] = useState(new Set())

  // Fetch recipe cost when recipeId, totalBlocks, or blockWeightKg changes
  // The Edge Function calculates correct quantities using ratio-based formula
  useEffect(() => {
    if (recipeId && totalBlocks > 0) {
      calculateCost(recipeId, totalBlocks, blockWeightKg)
    }
  }, [recipeId, totalBlocks, blockWeightKg, calculateCost])

  // Notify parent of allocation status
  useEffect(() => {
    if (recipeCost && onAllocationChange) {
      onAllocationChange({
        allIngredientsAvailable: recipeCost.all_ingredients_available,
        totalCost: recipeCost.total_cost,
        costPerBlock: recipeCost.cost_per_block,
        totalBatchKg: recipeCost.total_batch_kg,
        totalParts: recipeCost.total_parts,
        totalDryWeightKg: recipeCost.total_dry_weight_kg,
        ingredients: recipeCost.ingredients,
      })
    }
  }, [recipeCost, onAllocationChange])

  const toggleIngredient = (ingredientId) => {
    setExpandedIngredients((prev) => {
      const next = new Set(prev)
      if (next.has(ingredientId)) {
        next.delete(ingredientId)
      } else {
        next.add(ingredientId)
      }
      return next
    })
  }

  // Loading state
  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Calculating material allocation...
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </CardContent>
      </Card>
    )
  }

  // Error state
  if (error) {
    return (
      <Alert variant="destructive" className={className}>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          Failed to calculate material allocation: {error}
        </AlertDescription>
      </Alert>
    )
  }

  // No recipe selected
  if (!recipeCost) {
    return null
  }

  const { ingredients, total_cost, cost_per_block, all_ingredients_available } = recipeCost
  const trackedIngredients = ingredients.filter((i) => i.track_inventory)
  const untrackedIngredients = ingredients.filter((i) => !i.track_inventory)
  const insufficientIngredients = ingredients.filter((i) => !i.sufficient_stock)

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Package className="h-5 w-5" />
            Material Allocation Preview
          </CardTitle>
          {all_ingredients_available ? (
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Stock available
            </Badge>
          ) : (
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
              <AlertTriangle className="h-3 w-3 mr-1" />
              {insufficientIngredients.length} item{insufficientIngredients.length !== 1 ? 's' : ''} low
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Insufficient stock warning */}
        {!all_ingredients_available && (
          <Alert className="bg-amber-50 border-amber-200">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              <span className="font-medium">Insufficient stock</span> for some ingredients.
              Batch creation will be blocked until stock is available.
            </AlertDescription>
          </Alert>
        )}

        {/* Tracked ingredients */}
        <div>
          {trackedIngredients.map((ingredient) => (
            <IngredientAllocation
              key={ingredient.ingredient_id}
              ingredient={ingredient}
              isExpanded={expandedIngredients.has(ingredient.ingredient_id)}
              onToggle={() => toggleIngredient(ingredient.ingredient_id)}
            />
          ))}
        </div>

        {/* Untracked ingredients (e.g., water) */}
        {untrackedIngredients.length > 0 && (
          <div className="pt-2 border-t">
            <div className="text-xs text-content-tertiary uppercase tracking-wide mb-2">
              Not tracked in inventory
            </div>
            {untrackedIngredients.map((ingredient) => (
              <IngredientAllocation
                key={ingredient.ingredient_id}
                ingredient={ingredient}
                isExpanded={false}
                onToggle={() => {}}
              />
            ))}
          </div>
        )}

        {/* Cost summary */}
        <div className="pt-4 border-t space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Scale className="h-4 w-4 text-content-tertiary" />
              <span className="text-sm text-content-secondary">Estimated Material Cost</span>
            </div>
            <span className="text-lg font-bold">{formatCurrency(total_cost)}</span>
          </div>
          <div className="flex items-center justify-between text-sm text-content-tertiary">
            <span>Per Block</span>
            <span className="font-mono">{formatCurrency(cost_per_block)}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
